<script setup lang="ts">
import { onBeforeUnmount, watch } from 'vue'

import { useBackdropClose } from '@/lib/backdropClose'
import { useDialogsStore } from '@/stores/dialogs'

/**
 * 通用對話框（admin console）：Teleport 到 body、背景點擊／Esc 關閉、開窗時鎖 body 捲動。
 *
 * - z-index **1050**——DialogsHost（confirm／notify）是 1100，要能疊在本 modal 之上。
 * - 內容以 v-if 掛載：關窗即卸載。ModulePicker／QuotaEditor 的 initFrom 只在掛載時跑，
 *   開窗前先把帶入值準備好再開（既有的 :key 重掛招數在對話框內照樣有效）。
 * - `closeOnBackdrop`（批147，預設 true＝行為不變）：false 時**點背景不關**，× 與 Esc 照常。
 *   給「表單已有輸入」與「明文只顯示一次」這類誤點就會丟東西的對話框用。
 */
const props = withDefaults(
  defineProps<{ open: boolean; title?: string; width?: string; closeOnBackdrop?: boolean }>(),
  {
    title: '',
    width: '760px',
    closeOnBackdrop: true,
  },
)
const emit = defineEmits<{ close: [] }>()
const dialogs = useDialogsStore()

function onKey(e: KeyboardEvent) {
  // 確認框（DialogsHost，疊在本 modal 之上）開著時不理 Esc（R3-19）：不然 Esc 會穿透關掉底下的 modal，
  // 又觸發一次 @close（例如「還沒複製明碼」的確認）。
  if (e.key === 'Escape' && !dialogs.current) emit('close')
}

// 背景關閉不用 `@click.self`——在框內選字拖到框外放開會誤關（見 lib/backdropClose）。
const backdropClose = useBackdropClose(() => {
  if (props.closeOnBackdrop) emit('close')
})
function attach() {
  window.addEventListener('keydown', onKey)
  document.body.style.overflow = 'hidden'
}
function detach() {
  window.removeEventListener('keydown', onKey)
  document.body.style.overflow = ''
}
watch(
  () => props.open,
  (o) => (o ? attach() : detach()),
  { immediate: true },
)
onBeforeUnmount(detach)
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="modal-backdrop" v-on="backdropClose">
      <div class="modal-card" :style="{ width }" role="dialog" aria-modal="true">
        <div class="modal-head">
          <h3>{{ title }}</h3>
          <button type="button" class="modal-x" aria-label="關閉" @click="emit('close')">×</button>
        </div>
        <div class="modal-body"><slot /></div>
        <div v-if="$slots.foot" class="modal-foot"><slot name="foot" /></div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1050;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(2px);
  display: grid;
  /* ★ 批147：欄軌要明寫 `minmax(0, 1fr)`。不寫的話隱含欄軌是 auto、會照卡片的固定 `width`
     （760／720px）長寬，`.modal-card` 的 `max-width: 100%` 解析成「100% 的 720px」——
     手機寬（390px）時整張卡片超出畫面一半，右半邊的勾選框與按鈕點不到。
     欄軌釘成容器寬之後 100% 才是真的「最多到畫面寬」；桌機寬度下行為不變（照樣置中）。 */
  grid-template-columns: minmax(0, 1fr);
  place-items: center;
  padding: var(--sp-6);
}
.modal-card {
  max-width: 100%;
  max-height: calc(100vh - 2 * var(--sp-6));
  display: flex;
  flex-direction: column;
  background: var(--bg-panel);
  border: 1px solid var(--border);
  border-radius: var(--r-xl);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  animation: modal-pop var(--dur-base) var(--ease);
}
@keyframes modal-pop {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
.modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-2);
  padding: var(--sp-4) var(--sp-5);
  border-bottom: 1px solid var(--border);
}
.modal-head h3 {
  margin: 0;
  font-size: var(--fs-15);
  color: var(--fg);
}
.modal-x {
  border: none;
  background: none;
  color: var(--fg-4);
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;
}
.modal-x:hover {
  color: var(--fg);
}
.modal-body {
  padding: var(--sp-4) var(--sp-5);
  overflow: auto;
  min-height: 0;
}
.modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: var(--sp-2);
  padding: var(--sp-3) var(--sp-5);
  border-top: 1px solid var(--border);
}
/* 手機（批171）：卡片撐滿畫面寬（`width` 是 inline style，要 !important 才蓋得掉）、高度到可視高為止，
   內距收窄把寬度留給欄位；底部按鈕放不下時換行而不是被擠出畫面。 */
@media (--mobile) {
  .modal-backdrop {
    padding: var(--sp-2) max(var(--sp-2), env(safe-area-inset-right)) var(--sp-2) max(var(--sp-2), env(safe-area-inset-left));
  }
  .modal-card {
    width: 100% !important;
    max-height: calc(100vh - 2 * var(--sp-2));
    max-height: calc(100dvh - 2 * var(--sp-2));
    border-radius: var(--r-lg);
  }
  .modal-head {
    padding: var(--sp-3) var(--sp-4);
  }
  .modal-body {
    padding: var(--sp-3) var(--sp-4);
  }
  /* 表格的捲動容器用負邊距貼齊外框（ui.css 的 `.tbl-scroll` 是照卡片的 --sp-5 寫的）：
     內距收窄之後要跟著收，否則左右各凸出 4px、整個對話框長出橫捲。 */
  .modal-body :deep(.tbl-scroll) {
    margin: 0 calc(-1 * var(--sp-4));
    padding: 0 var(--sp-4);
  }
  .modal-foot {
    flex-wrap: wrap;
    padding: var(--sp-3) var(--sp-4);
  }
}
</style>
